import {
  IEndClosureAction,
  IStartClosureAction,
  IWorkflowStep,
  PermitActionType,
  WorkflowStepType,
} from '~/client/graph'

const getStartClosureAction = (
  workflowSteps: IWorkflowStep[],
): IStartClosureAction =>
  workflowSteps
    .flatMap(step => step.actions || [])
    .find(action => action.type === PermitActionType.StartClosureAction)

const getLastStep = (workflowSteps: IWorkflowStep[]): IWorkflowStep => {
  return workflowSteps.at(-1)
}

const removeEndClosureActions = (
  workflowSteps: IWorkflowStep[],
): IWorkflowStep[] => {
  return workflowSteps.map(step => ({
    ...step,
    actions: (step.actions || []).filter(
      action => action.type !== PermitActionType.EndClosureAction,
    ),
  }))
}

const validateActionLocationFields = (
  action: any,
  availableFieldIds: string[],
): boolean => {
  if (!action.locationFieldIds?.length) {
    return true
  }

  action.locationFieldIds = action.locationFieldIds.filter((id: string) =>
    availableFieldIds.includes(id),
  )

  const requiresLocationFields =
    action.type === PermitActionType.StartClosureAction ||
    action.type === PermitActionType.EndClosureAction

  return !requiresLocationFields || action.locationFieldIds.length > 0
}

const createEndClosureAction = (
  startClosureAction: IStartClosureAction,
): IEndClosureAction => {
  return {
    type: PermitActionType.EndClosureAction,
    locationFieldIds:
      startClosureAction.locationFieldsMap?.reduce((list, value) => {
        if (!value.isDisabled) {
          list.push(value.fieldId)
        }
        return list
      }, [] as string[]) || [],
  }
}

const getAllFieldIds = (workflowSteps: IWorkflowStep[]): string[] =>
  workflowSteps.reduce((ids, step) => {
    if (step.fields) {
      ids.push(...step.fields.filter(field => field.id).map(field => field.id))
    }
    if (step.conditionalFields) {
      step.conditionalFields.forEach(cf => {
        if (cf.fieldId) ids.push(cf.fieldId)
        if (cf.values) ids.push(...cf.values.filter(v => v.id).map(v => v.id))
      })
    }
    return ids
  }, [] as string[])

const cleanupStartClosureActions = (
  workflowSteps: IWorkflowStep[],
): IWorkflowStep[] => {
  let found = false
  const lastStepIndex = workflowSteps.length - 1
  return workflowSteps.map((step, index) => {
    if (!step.actions) return step

    const actions = step.actions.filter(action => {
      if (action.type !== PermitActionType.StartClosureAction) return true
      if (index === lastStepIndex || found) return false
      if (step.type === WorkflowStepType.RecurringInspection) return false
      found = true
      return true
    })

    return { ...step, actions }
  })
}

export const updateWorkflowActions = (
  workflowSteps: IWorkflowStep[],
): IWorkflowStep[] => {
  let updatedSteps = [...workflowSteps]
  const allFieldIds = getAllFieldIds(updatedSteps)

  updatedSteps = updatedSteps.map(step => {
    if (!step.actions) return step

    const validActions = step.actions.filter(action =>
      validateActionLocationFields(action, allFieldIds),
    )

    return { ...step, actions: validActions }
  })

  updatedSteps = cleanupStartClosureActions(updatedSteps)
  updatedSteps = removeEndClosureActions(updatedSteps)

  const startClosureAction = getStartClosureAction(updatedSteps)
  if (startClosureAction) {
    const lastStep = getLastStep(updatedSteps)
    if (lastStep) {
      const lastStepIndex = updatedSteps.length - 1
      if (!updatedSteps[lastStepIndex].actions) {
        updatedSteps[lastStepIndex] = {
          ...updatedSteps[lastStepIndex],
          actions: [],
        }
      }

      const endClosureAction = createEndClosureAction(startClosureAction)
      updatedSteps[lastStepIndex].actions.push(endClosureAction)
    }
  }

  return updatedSteps
}
